import { Avatar } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProfile, updateProfile } from "../Redux/Action/AuthAction";
import CircularProgress from "@mui/material/CircularProgress";
import { FaPen } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import useTitle from "../Hooks/useTitle";

const Profile = () => {
  useTitle('Profile');
  const dispatch = useDispatch();
  const { t } = useTranslation("profilePage");
  const Auth = useSelector((state) => state.Auth);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [postal_code, setPostal_Code] = useState("");
  const [city, setCity] = useState("");
  const [isEdited, setIsEdited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    setLoadingProfile(true);
    dispatch(getProfile()).then((res) => {
      if (res) {
        setName(res.name);
        setEmail(res.email);
        setPhone(res.phone);
        setAddress(res.address);
        setPostal_Code(res.postal_code);
        setCity(res.city);
        setImagePreview(res.profile_pic || "/default-profile.png");
      }
    }).finally(() => {
      setLoadingProfile(false);
    });
  }, [dispatch]);

  useEffect(() => {
    if (
      name !== Auth.profile.name ||
      email !== Auth.profile.email ||
      phone !== Auth.profile.phone ||
      address !== Auth.profile.address ||
      postal_code !== Auth.profile.postal_code ||
      city !== Auth.profile.city ||
      profileImage !== null
    ) {
      setIsEdited(true);
    } else {
      setIsEdited(false);
    }
  }, [
    name,
    email,
    phone,
    address,
    postal_code,
    profileImage,
    city,
    Auth.profile,
  ]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const updateUserProfile = (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    formData.append("name", name);
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("address", address);
    formData.append("postal_code", postal_code);
    formData.append("city", city);
    if (profileImage) {
      formData.append("profile_pic", profileImage);
    }

    dispatch(updateProfile(formData)).finally(() => {
      setIsEdited(false);
      setIsLoading(false);
    });
  };

  const resetForm = () => {
    setName(Auth.profile.name);
    setEmail(Auth.profile.email);
    setPhone(Auth.profile.phone);
    setAddress(Auth.profile.address);
    setPostal_Code(Auth.profile.postal_code);
    setCity(Auth.profile.city);
    setImagePreview(Auth.profile.profile_pic || "/default-profile.png");
    setIsEdited(false);
  };

  return (
    <div className="p-8 py-6 mt-[76px] sm:ml-[300px]">
      <div className="font-semibold text-2xl mb-7">{t('profile')}</div>
      <div className="w-full lg:w-[70%] bg-white rounded-2xl min-h-[300px]">
        {loadingProfile ? (
          <div className="flex justify-center items-center h-[300px]">
            <CircularProgress />
          </div>
        ) : (
          <form
            onSubmit={updateUserProfile}
            className="bg-white py-6 flex flex-col rounded-[20px]"
            method="POST"
          >
            <div className="grid gap-5">
              <div className="relative flex justify-center">
                <label
                  htmlFor="profile-image"
                  className="cursor-pointer relative border rounded-full"
                >
                  <Avatar src={imagePreview} sx={{ width: 100, height: 100 }} />
                  <div className="absolute bottom-0 right-0 bg-[#3686FF] rounded-full shadow-md h-8 w-8 flex justify-center items-center">
                    <FaPen className="text-[#fff]" size={15} />
                  </div>
                  <input
                    type="file"
                    id="profile-image"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                </label>
              </div>

              <div className="flex flex-col gap-5 px-6">
                <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <span className="text-[14px] font-[500] text-[#131415] text-left">
                      {t('name')}
                    </span>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      type="text"
                      className="border border-[#c3cbd7] py-[10px] rounded-[10px] px-2 text-[14px] w-full"
                      name="name"
                      placeholder={t('placeholder_name')}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-[14px] font-[500] text-[#131415] text-left">
                      {t('email')}
                    </span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="border border-[#c3cbd7] py-[10px] rounded-[10px] px-3 text-[14px] w-full"
                      name="email"
                      placeholder={t('placeholder_email')}
                    />
                  </div>
                </div>

                <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <span className="text-[14px] font-[500] text-[#131415] text-left">
                      {t('phone_no')}
                    </span>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      type="number"
                      className="border border-[#c3cbd7] py-[10px] rounded-[10px] px-2 text-[14px] w-full"
                      name="phone"
                      placeholder={t('placeholder_phone_no')}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-[14px] font-[500] text-[#131415] text-left">
                      {t('address')}
                    </span>
                    <input
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      type="text"
                      className="border border-[#c3cbd7] py-[10px] rounded-[10px] px-3 text-[14px] w-full"
                      name="address"
                      placeholder={t('placeholder_address')}
                    />
                  </div>
                </div>

                <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <span className="text-[14px] font-[500] text-[#131415] text-left">
                      {t('postal_code')}
                    </span>
                    <input
                      value={postal_code}
                      onChange={(e) => setPostal_Code(e.target.value)}
                      type="text"
                      className="border border-[#c3cbd7] py-[10px] rounded-[10px] px-2 text-[14px] w-full"
                      name="postal_code"
                      placeholder={t('placeholder_postal_code')}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-[14px] font-[500] text-[#131415] text-left">
                      {t('city')}
                    </span>
                    <input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      type="text"
                      className="border border-[#c3cbd7] py-[10px] rounded-[10px] px-3 text-[14px] w-full"
                      name="city"
                      placeholder={t('placeholder_city')}
                    />
                  </div>
                </div>
              </div>

              <hr className="mt-5" />

              <div className="px-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-[#3686FF] rounded-lg h-[45px] text-[14px] w-full sm:max-w-[150px]"
                  style={{ border: "1px solid #3686FF" }}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={!isEdited || isLoading}
                  className={`bg-[#3686FF] text-white rounded-lg h-[45px] text-[14px] w-full sm:max-w-[150px] flex justify-center items-center ${!isEdited || isLoading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                >
                  {isLoading ? (
                    <CircularProgress size={25} sx={{ color: "white" }} />
                  ) : (
                    `${t('save')}`
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Profile;
